import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Helmet } from 'react-helmet-async';

import config from '@src/config';
import leadGurusLogo from '@src/assets/logo-full-dark.png';
import { toast } from '@src/components/core/toaster';
import {
  fetchProspectEstimate,
  fetchProspectZipDemographics,
  fetchProspectParentCompany,
  submitProspect,
} from '@src/api/actions/prospects';
import ProjectionZipMap from './components/ProjectionZipMap';

const STEPS = ['Apply', 'Contact Info', 'ZIP Codes', 'Budget', 'Projections'];

const PARENT_SLUG = 'five-star-bath-solutions';
const VERTICAL = 'Bath';
const MIN_BUDGET = 3500;

// ---------------------------------------------------------------------------
// ZIP parser — single column, one ZIP per line. Supports comma/space separated.
// ---------------------------------------------------------------------------
function parseZipsSimple(text) {
  return text
    .split(/[\s,]+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((raw) => {
      let zip = raw;
      if (/^\d{4}$/.test(zip)) zip = `0${zip}`;
      return zip;
    })
    .filter((z) => /^\d{5}$/.test(z));
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------
const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));
const fmtCurrency = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`);
const fmtPct = (n) => (n == null || !Number.isFinite(n) ? '—' : `${(Number(n) * 100).toFixed(1)}%`);
const fmtDec = (n) => (n == null || !Number.isFinite(n) ? '—' : Math.round(n).toLocaleString('en-US'));
const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function GetStartedFiveStarPage() {
  const [activeStep, setActiveStep] = useState(0);

  const [contactForm, setContactForm] = useState({
    location: '',
    contact_name: '',
    contact_email: '',
    phone: '',
  });

  const [zipText, setZipText] = useState('');
  const [zipCodes, setZipCodes] = useState([]);

  const [budget, setBudget] = useState('');

  const [projectionSummary, setProjectionSummary] = useState(null);
  const [demographics, setDemographics] = useState(null);
  const [isLoadingProjection, setIsLoadingProjection] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [projectionsUrl, setProjectionsUrl] = useState(null);

  const [parentLogo, setParentLogo] = useState(null);

  // Fetch Five Star parent company logo on mount
  useEffect(() => {
    fetchProspectParentCompany(PARENT_SLUG)
      .then((res) => setParentLogo(res?.data?.logo_url || null))
      .catch(() => {});
  }, []);

  // Parse zips when text changes
  useEffect(() => {
    setZipCodes(parseZipsSimple(zipText));
  }, [zipText]);

  // Build full zip rows decorated with territory + vertical
  const zipRows = useMemo(
    () => zipCodes.map((zip) => ({ zip, territory: contactForm.location, vertical: VERTICAL, brand: '' })),
    [zipCodes, contactForm.location],
  );

  const budgetNum = Number(budget) || 0;

  const loadProjections = useCallback(async () => {
    if (!zipRows.length || budgetNum <= 0) return;
    setIsLoadingProjection(true);
    try {
      const verticalBudgets = { [VERTICAL]: budgetNum };
      const [estimateRes, demoRes] = await Promise.all([
        fetchProspectEstimate({
          budget: budgetNum,
          verticals: [VERTICAL],
          vertical_budgets: verticalBudgets,
          scenario: 'base',
          zips: zipRows.map((r) => r.zip),
        }),
        fetchProspectZipDemographics({
          zip_rows: zipRows,
          verticals: [VERTICAL],
        }),
      ]);
      setProjectionSummary(estimateRes?.data?.summary || null);
      setDemographics(demoRes?.data || null);
    } catch {
      toast.error('Failed to load projections. Please try again.');
    } finally {
      setIsLoadingProjection(false);
    }
  }, [zipRows, budgetNum]);

  const handleNext = () => {
    if (activeStep === 3) {
      // Moving to projections
      setActiveStep(4);
      loadProjections();
      return;
    }
    setActiveStep((p) => p + 1);
  };

  const handleBack = () => setActiveStep((p) => p - 1);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const companyName = `Five Star - ${contactForm.location}`.trim();
      const res = await submitProspect({
        company_name: companyName,
        contact_name: contactForm.contact_name,
        contact_email: contactForm.contact_email,
        phone: contactForm.phone,
        website: '',
        verticals: [VERTICAL],
        zip_data: zipRows,
        budget: budgetNum,
        vertical_budgets: { [VERTICAL]: budgetNum },
        projection_snapshot: projectionSummary,
        demographics_snapshot: demographics
          ? { territories: demographics.territories, totals: demographics.totals }
          : null,
        zip_rows_snapshot: zipRows,
        application_source: 'five_star',
      });
      if (res?.data?.projections_url) setProjectionsUrl(res.data.projections_url);
      setSubmitted(true);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = useMemo(() => {
    switch (activeStep) {
      case 0:
        return true;
      case 1:
        return !!(
          contactForm.location.trim() &&
          contactForm.contact_name.trim() &&
          isValidEmail(contactForm.contact_email)
        );
      case 2:
        return zipCodes.length > 0;
      case 3:
        return budgetNum >= MIN_BUDGET;
      case 4:
        return !!projectionSummary;
      default:
        return false;
    }
  }, [activeStep, contactForm, zipCodes, budgetNum, projectionSummary]);

  const header = (
    <Box sx={{ bgcolor: '#111827', py: 2, px: 3 }}>
      <Container maxWidth="lg">
        <Stack direction="row" alignItems="center" spacing={2}>
          <img src={leadGurusLogo} alt="Lead Gurus" style={{ height: 28 }} />
          {parentLogo && (
            <>
              <Typography sx={{ color: '#9ca3af', fontSize: 18, fontWeight: 300 }}>×</Typography>
              <img
                src={parentLogo}
                alt="Five Star Bath Solutions"
                style={{ height: 32, background: '#fff', padding: '4px 8px', borderRadius: 4 }}
              />
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );

  if (submitted) {
    const leads = projectionSummary?.estimated_leads ? Math.round(projectionSummary.estimated_leads) : null;
    const revenue = projectionSummary?.estimated_revenue ? fmtCurrency(projectionSummary.estimated_revenue) : null;
    const companyName = `Five Star - ${contactForm.location}`.trim();

    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <Helmet><title>Application Received | {config.SITE.title}</title></Helmet>
        {header}

        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom>Application Received</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Thank you for your interest in the Lead Gurus × Five Star Bath Solutions program.
              Our team will review your territory and availability, and follow up at <strong>{contactForm.contact_email}</strong>.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Branch</Typography>
                <Typography fontWeight={600} variant="body2">{companyName}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Vertical</Typography>
                <Typography fontWeight={600} variant="body2">{VERTICAL}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">ZIP Codes</Typography>
                <Typography fontWeight={600} variant="body2">{zipCodes.length}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Monthly Budget</Typography>
                <Typography fontWeight={600} variant="body2">{fmtCurrency(budgetNum)}</Typography>
              </Grid>
            </Grid>

            {(leads || revenue) && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Your Estimated Projections</Typography>
                <Stack direction="row" justifyContent="center" spacing={4}>
                  {leads && (
                    <Box>
                      <Typography variant="h5" fontWeight={700}>{leads}</Typography>
                      <Typography variant="caption" color="text.secondary">Leads/mo</Typography>
                    </Box>
                  )}
                  {revenue && (
                    <Box>
                      <Typography variant="h5" fontWeight={700}>{revenue}</Typography>
                      <Typography variant="caption" color="text.secondary">Est. Revenue/mo</Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            )}

            {projectionsUrl && (
              <Button
                variant="contained"
                href={projectionsUrl}
                target="_blank"
                rel="noopener"
                sx={{ mb: 3 }}
              >
                View Full Projections
              </Button>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>What Happens Next</Typography>
            <Stack spacing={1.5} sx={{ textAlign: 'left' }}>
              {[
                { num: '1', title: 'Application Review', text: 'Our team confirms your territory and availability with the Five Star network' },
                { num: '2', title: 'Contract Sent', text: 'If approved, we\'ll send your agreement over for signature' },
                { num: '3', title: 'Onboarding', text: 'Once signed, we set up your campaigns, creative, landing pages, and tracking' },
                { num: '4', title: 'Launch', text: 'Leads start flowing within 7-10 business days of signing' },
              ].map((s) => (
                <Stack key={s.num} direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#ff680d', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, flexShrink: 0, mt: 0.25 }}>
                    {s.num}
                  </Box>
                  <Typography variant="body2"><strong>{s.title}</strong> — {s.text}</Typography>
                </Stack>
              ))}
            </Stack>

            {projectionsUrl && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
                Share your projections with your team: <a href={projectionsUrl} target="_blank" rel="noopener" style={{ color: '#ff680d' }}>{projectionsUrl}</a>
              </Typography>
            )}
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet><title>Get Started — Five Star Bath Solutions | {config.SITE.title}</title></Helmet>

      {header}

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Apply to Join — Five Star Bath Solutions × Lead Gurus
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          Submit your branch details to get started with the exclusive branded leads program built specifically for Five Star Bath Solutions owners.
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            {activeStep === 0 && <StepIntro />}
            {activeStep === 1 && <StepContact form={contactForm} onChange={setContactForm} />}
            {activeStep === 2 && (
              <StepZips
                zipText={zipText}
                setZipText={setZipText}
                zipCodes={zipCodes}
                location={contactForm.location}
              />
            )}
            {activeStep === 3 && (
              <StepBudget budget={budget} setBudget={setBudget} budgetNum={budgetNum} />
            )}
            {activeStep === 4 && (
              <StepProjections
                summary={projectionSummary}
                demographics={demographics}
                isLoading={isLoadingProjection}
                onRefresh={loadProjections}
              />
            )}
          </CardContent>
        </Card>

        <Stack direction="row" justifyContent="space-between">
          <Button disabled={activeStep === 0} onClick={handleBack} variant="outlined">Back</Button>
          {activeStep < STEPS.length - 1 ? (
            <Button onClick={handleNext} variant="contained" disabled={!canProceed}>
              {activeStep === 0 ? 'Start Application' : 'Next'}
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!canProceed || isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={18} /> : <RocketLaunchIcon />}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          )}
        </Stack>
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step 0: Intro — Program Overview, tailored for Five Star owners
// ---------------------------------------------------------------------------
function StepIntro() {
  const highlights = [
    {
      icon: <VerifiedIcon color="primary" />,
      title: 'Built for Five Star Bath Solutions Owners',
      text: 'We currently work with 50+ Five Star branches across the country, generating over $1.2M/month in net revenue for the network. Our creative, landing pages, and self-book system are tuned specifically to Five Star\'s brand and offer.',
    },
    {
      icon: <StarIcon color="primary" />,
      title: 'Exclusive, Branded Leads — Never Shared',
      text: 'Every lead is generated under your Five Star brand and routed only to you. No shared leads, no resold leads, no competing with other branches in your market.',
    },
    {
      icon: <CheckCircleIcon color="primary" />,
      title: 'Scale at Your Pace',
      text: 'Branches start as low as $3,500/month and several of our top Five Star partners scale to $15,000+/month with us as their pipelines fill. You set the budget that fits your capacity.',
    },
  ];

  return (
    <Stack spacing={3}>
      <Typography variant="h5" fontWeight={700}>The Five Star Bath × Lead Gurus Program</Typography>
      <Typography color="text.secondary">
        Lead Gurus is the exclusive lead-gen partner for 50+ Five Star Bath Solutions branches.
        We handle the ad spend, creative, landing pages, and tracking — you handle the appointments.
      </Typography>

      <Divider />

      <Stack spacing={3}>
        {highlights.map((h) => (
          <Stack key={h.title} direction="row" spacing={2} alignItems="flex-start">
            <Box sx={{ mt: 0.5 }}>{h.icon}</Box>
            <Box>
              <Typography fontWeight={600}>{h.title}</Typography>
              <Typography variant="body2" color="text.secondary">{h.text}</Typography>
            </Box>
          </Stack>
        ))}
      </Stack>

      <Divider />

      <Paper variant="outlined" sx={{ p: 2.5, bgcolor: '#f8fdf9' }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>What we look for in a partner</Typography>
        <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, mb: 0 }}>
          <li>A minimum monthly ad budget of <strong>$3,500</strong> — top Five Star branches with us spend $15,000+/month</li>
          <li>A sizable service area — the larger the market, the better our campaigns perform</li>
          <li>Service coverage across a defined set of ZIP codes</li>
          <li>A team that can follow up on leads quickly — speed to lead matters</li>
          <li>Willingness to let our proven Five Star creative and self-book system work</li>
        </Typography>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        This application takes about 5 minutes. You'll enter your branch location, service ZIPs, and budget — and see estimated lead projections before you submit.
      </Typography>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 1: Contact Info
// ---------------------------------------------------------------------------
function formatPhone(value) {
  const digits = (value || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function StepContact({ form, onChange }) {
  const update = (field) => (e) => onChange({ ...form, [field]: e.target.value });
  const previewName = form.location.trim() ? `Five Star - ${form.location.trim()}` : '';
  return (
    <Stack spacing={3}>
      <Typography variant="h6">Your Branch Information</Typography>
      <Typography variant="body2" color="text.secondary">
        Tell us about your Five Star Bath Solutions branch so our team can review your application.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Branch Location"
            placeholder="e.g. Tampa, FL"
            value={form.location}
            onChange={update('location')}
            fullWidth
            required
            helperText={previewName ? `Your branch will be listed as: ${previewName}` : 'City and state of your Five Star branch'}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Your Name" value={form.contact_name} onChange={update('contact_name')} fullWidth required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Email"
            type="email"
            value={form.contact_email}
            onChange={update('contact_email')}
            fullWidth
            required
            error={!!form.contact_email && !isValidEmail(form.contact_email)}
            helperText={form.contact_email && !isValidEmail(form.contact_email) ? 'Enter a valid email address' : ''}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Phone (optional)"
            placeholder="(555) 123-4567"
            value={form.phone}
            onChange={(e) => onChange({ ...form, phone: formatPhone(e.target.value) })}
            fullWidth
          />
        </Grid>
      </Grid>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 2: ZIP Codes — single column, just ZIPs
// ---------------------------------------------------------------------------
function StepZips({ zipText, setZipText, zipCodes, location }) {
  const previewZips = zipCodes.slice(0, 12);

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Your Service ZIP Codes</Typography>
      <Typography variant="body2" color="text.secondary">
        Paste the ZIP codes you service, one per line. Your territory will be set to{' '}
        <strong>{location || 'your branch location'}</strong> and your vertical to <strong>Bath</strong>.
      </Typography>

      <TextField
        multiline
        minRows={8}
        maxRows={20}
        placeholder={'33601\n33602\n33603\n33604\n…'}
        value={zipText}
        onChange={(e) => setZipText(e.target.value)}
        fullWidth
        variant="outlined"
      />

      {zipCodes.length > 0 && (
        <Alert severity="success" variant="outlined">
          <strong>{zipCodes.length}</strong> ZIP code{zipCodes.length !== 1 ? 's' : ''} parsed
        </Alert>
      )}

      {zipCodes.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>Preview</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {previewZips.map((z) => (
              <Chip key={z} label={z} size="small" variant="outlined" />
            ))}
            {zipCodes.length > previewZips.length && (
              <Chip label={`+${zipCodes.length - previewZips.length} more`} size="small" />
            )}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Budget — single field for Bath, $3,500 minimum
// ---------------------------------------------------------------------------
function StepBudget({ budget, setBudget, budgetNum }) {
  const belowMin = budgetNum > 0 && budgetNum < MIN_BUDGET;
  return (
    <Stack spacing={3}>
      <Typography variant="h6">Your Monthly Investment</Typography>
      <Typography variant="body2" color="text.secondary">
        Indicate the monthly ad budget you're prepared to invest. This helps our team evaluate fit and availability for your territory.
      </Typography>

      <Alert severity="info" variant="outlined">
        Our minimum monthly investment for Five Star branches is <strong>{fmtCurrency(MIN_BUDGET)}/month</strong>.
        Several Five Star branches with us are spending $15,000+/month — you can scale up as your pipeline fills.
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Monthly Bath Budget"
            type="number"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            fullWidth
            error={belowMin}
            helperText={belowMin ? `Minimum is ${fmtCurrency(MIN_BUDGET)}/month` : ''}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5', textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">Total Monthly Investment</Typography>
        <Typography variant="h5" fontWeight={700}>{fmtCurrency(budgetNum)}</Typography>
      </Paper>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 4: Projections (unchanged from main flow)
// ---------------------------------------------------------------------------
function StepProjections({ summary, demographics, isLoading, onRefresh }) {
  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography color="text.secondary">Calculating your projections...</Typography>
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography color="text.secondary">No projection data available.</Typography>
        <Button onClick={onRefresh} sx={{ mt: 2 }}>Retry</Button>
      </Box>
    );
  }

  const features = demographics?.features || [];
  const territories = demographics?.territories || [];
  const totals = demographics?.totals || {};
  const a = summary.assumptions || {};
  const leads = Math.round(summary.estimated_leads || 0);
  const setAppts = summary.verticals?.reduce((s, v) => s + (v.set_appointments || 0), 0);
  const issues = summary.verticals?.reduce((s, v) => s + (v.issues || 0), 0);
  const demos = summary.verticals?.reduce((s, v) => s + (v.demos || 0), 0);
  const netSold = summary.verticals?.reduce((s, v) => s + (v.net_sold || 0), 0);
  const comPct = summary.estimated_revenue ? ((summary.budget / summary.estimated_revenue) * 100).toFixed(1) + '%' : '—';

  const blocks = [
    { label: 'Total Budget', value: fmtCurrency(summary.budget), sub: summary.budget ? `~${fmtCurrency(summary.budget / 30)}/day` : null },
    { label: 'Leads & CPL', value: `${fmt(leads)} / ${summary.assumed_cpl ? fmtCurrency(summary.assumed_cpl) : '—'}`, sub: 'Est. leads / Blended CPL' },
    { label: 'Set Appointments', value: fmtDec(setAppts), sub: a.set_rate ? `Set rate ${fmtPct(a.set_rate)}` : null },
    { label: 'Issues', value: fmtDec(issues), sub: a.issue_rate ? `Issue rate ${fmtPct(a.issue_rate)}` : null },
    { label: 'Demos', value: fmtDec(demos), sub: a.demo_rate ? `Demo rate ${fmtPct(a.demo_rate)}` : null },
    { label: 'Net Sold', value: fmtDec(netSold), sub: a.net_close_rate ? `Close rate ${fmtPct(a.net_close_rate)}` : null },
    { label: 'Avg Job Size', value: a.avg_job_size ? fmtCurrency(a.avg_job_size) : '—' },
    { label: 'Net Revenue', value: fmtCurrency(summary.estimated_revenue) },
    { label: 'Cost of Marketing', value: comPct },
  ];

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Your Projections</Typography>

      {totals.population != null && totals.population < 1000000 && (
        <Alert severity="warning" variant="outlined" icon={<WarningAmberIcon />}>
          Your total serviceable population is <strong>{fmt(totals.population)}</strong>, which is under 1 million.
          Adding more ZIP codes will improve campaign performance.
        </Alert>
      )}

      <Alert severity="info" variant="outlined">
        These projections are estimates based on historical Five Star branch performance and industry benchmarks. Actual results may vary.
      </Alert>

      <Grid container spacing={1.5}>
        {blocks.map((b) => (
          <Grid item xs={6} sm={4} key={b.label}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}>
              <Typography color="text.secondary" variant="caption">{b.label}</Typography>
              <Typography variant="h6" fontWeight={700}>{b.value ?? '—'}</Typography>
              {b.sub && <Typography color="text.secondary" variant="caption">{b.sub}</Typography>}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {summary.verticals?.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Vertical</TableCell>
              <TableCell align="right">Budget</TableCell>
              <TableCell align="right">CPL</TableCell>
              <TableCell align="right">Leads</TableCell>
              <TableCell align="right">Set Appts</TableCell>
              <TableCell align="right">Net Sold</TableCell>
              <TableCell align="right">Net Revenue</TableCell>
              <TableCell align="right">CoM%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summary.verticals.map((v) => {
              const com = v.net_revenue ? ((v.budget / v.net_revenue) * 100).toFixed(1) + '%' : '—';
              return (
                <TableRow key={v.name}>
                  <TableCell>{v.name}</TableCell>
                  <TableCell align="right">{fmtCurrency(v.budget)}</TableCell>
                  <TableCell align="right">{fmtCurrency(v.scenario_cpl)}</TableCell>
                  <TableCell align="right">{fmtDec(v.leads)}</TableCell>
                  <TableCell align="right">{fmtDec(v.set_appointments)}</TableCell>
                  <TableCell align="right">{fmtDec(v.net_sold)}</TableCell>
                  <TableCell align="right">{fmtCurrency(v.net_revenue)}</TableCell>
                  <TableCell align="right">{com}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {features.length > 0 && (
        <Box sx={{ height: 400, borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
          <ProjectionZipMap features={features} territories={territories} />
        </Box>
      )}

      {territories.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Territory Summary</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Territory</TableCell>
                <TableCell>Vertical</TableCell>
                <TableCell align="right">ZIPs</TableCell>
                <TableCell align="right">Population</TableCell>
                <TableCell align="right">Med. Income</TableCell>
                <TableCell align="right">Med. Home Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {territories.map((t, i) => (
                <TableRow key={i}>
                  <TableCell>{t.territory}</TableCell>
                  <TableCell>{t.vertical}</TableCell>
                  <TableCell align="right">{fmt(t.zip_count)}</TableCell>
                  <TableCell align="right">{fmt(t.population)}</TableCell>
                  <TableCell align="right">{fmtCurrency(t.median_income)}</TableCell>
                  <TableCell align="right">{fmtCurrency(t.median_home_value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Stack>
  );
}
